import Supercluster from 'supercluster'
import useGoogleMaps from '../maps/useGoogleMaps'
import type { useFilterState } from '../filter/useFilterState'
import styles from './styles.json'
import type ClusterMarker from './ClusterMarkerClass'
import { useUpdateUrlQuery } from '~/composables/useUpdateUrlQuery'
import type { EstateListingEssentialData } from '~/types/api/EstateListing'

type ClusterData =
  | Supercluster.PointFeature<Supercluster.AnyProps>
  | Supercluster.ClusterFeature<Supercluster.AnyProps>

type MarkerType = {
  clusterData: ClusterData
  marker: google.maps.Marker
  openPopup: () => void
}

type ClusterMarkerType = {
  clusterData: ClusterData
  marker: ClusterMarker
}

function getClusterFromItems(items: EstateListingEssentialData[]) {
  const markersCluster = new Supercluster({
    radius: 60,
    extent: 256,
    minZoom: 2,
  })

  const itemsCloned = [...items].filter((item) => {
    return item.latitude && item.longitude
  })

  markersCluster.load(
    itemsCloned.map((item) => ({
      type: 'Feature',
      properties: {
        dataItem: item,
      },
      geometry: {
        type: 'Point',
        coordinates: [item.longitude, item.latitude],
      },
    }))
  )

  return markersCluster
}

async function getClusterMarker({
  map,
  mapCluster,
  clusterData,
}: {
  map: google.maps.Map
  mapCluster: Supercluster
  clusterData: ClusterData
}) {
  const { default: ClusterMarker } = await import('./ClusterMarkerClass')

  const clusterMarker = new ClusterMarker({
    position: new google.maps.LatLng(
      clusterData.geometry.coordinates[1],
      clusterData.geometry.coordinates[0]
    ),
    clusterData,
    clickCallback: () => {
      if (clusterData === null) return

      map?.setZoom(
        mapCluster.getClusterExpansionZoom(clusterData.properties.cluster_id)
      )

      map?.panTo({
        lat: clusterData.geometry.coordinates[1],
        lng: clusterData.geometry.coordinates[0],
      })
    },
  })

  return clusterMarker
}

function hideMarkerPopup({
  visiblePopup,
  selectedId,
}: {
  visiblePopup: Ref<any | null>
  selectedId: Ref<number | null>
}) {
  if (visiblePopup.value) {
    visiblePopup.value.setMap(null)
  }

  selectedId.value = null
  visiblePopup.value = null
}

function getPinMarker({
  Marker,
  map,
  clusterData,
  markerPinIcons,
  visiblePopup,
  selectedId,
}: {
  Marker: typeof google.maps.Marker
  map: google.maps.Map
  clusterData: ClusterData
  markerPinIcons: {
    default: google.maps.Icon
    hover: google.maps.Icon
  }
  visiblePopup: Ref<any | null>
  selectedId: Ref<number | null>
}) {
  const marker = new Marker({
    position: {
      lat: clusterData.geometry.coordinates[1],
      lng: clusterData.geometry.coordinates[0],
    },
    icon: markerPinIcons.default,
    shape: {
      coords: [15, 42, 30, 20, 29, 7, 22, 1, 15, 0, 8, 1, 1, 7, 0, 21],
      type: 'poly',
    },
  })

  async function openPopup() {
    if (selectedId.value === clusterData.properties.dataItem.id) {
      hideMarkerPopup({ visiblePopup, selectedId })
      return
    }

    if (visiblePopup.value) {
      hideMarkerPopup({ visiblePopup, selectedId })
    }

    selectedId.value = clusterData.properties.dataItem.id

    const { default: Popup } = await import('./PopupClass')

    const popup = new Popup({
      position: new google.maps.LatLng(
        clusterData.geometry.coordinates[1],
        clusterData.geometry.coordinates[0]
      ),
      estateEssentialData: clusterData.properties.dataItem,
      estateLink: getEstateDetailLink({
        estateType: clusterData.properties.dataItem.type,
        id: clusterData.properties.dataItem.id,
        slug: clusterData.properties.dataItem.slug,
      }),
      map,
      removeCallback: () => {
        hideMarkerPopup({ visiblePopup, selectedId })
      },
    })
    popup.setMap(map)

    visiblePopup.value = popup
  }

  marker.addListener('click', openPopup)
  marker.addListener('mouseover', () => marker.setIcon(markerPinIcons.hover))
  marker.addListener('mouseout', () => marker.setIcon(markerPinIcons.default))

  return {
    marker,
    openPopup,
  }
}

export function useEstateListingMap({
  filterState,
}: {
  filterState: ReturnType<typeof useFilterState>
}) {
  const updateUrlQuery = useUpdateUrlQuery()
  const googleMaps = useGoogleMaps()
  const markers = [] as MarkerType[]
  const clusterMarkers = [] as ClusterMarkerType[]
  const visiblePopup = ref<any | null>(null)
  const selectedId = ref<number | null>(null)
  const isInitialized = ref(false)

  let map = null as google.maps.Map | null
  let mapCluster = null as Supercluster | null
  let lastZoom: number | undefined

  async function loadLibs() {
    await googleMaps.initGoogleMaps()
  }

  const initialQueryValues = updateUrlQuery.getQueryByKey('map')
  const initialValues = {
    zoom: initialQueryValues?.zoom || 7,
    center: initialQueryValues?.center || {
      lat: 48.669,
      lng: 19.699,
    },
    popupId: initialQueryValues?.popupId || null,
  }

  function initMap(mapEl: HTMLElement) {
    if (googleMaps.instance.value === null) return

    const { Map } = googleMaps.instance.value.maps

    map = new Map(mapEl, {
      center: initialValues.center,
      zoom: initialValues.zoom,
      scrollwheel: true,
      disableDefaultUI: true,
      zoomControl: true,
      minZoom: 2,
      gestureHandling: 'greedy',
      styles,
    })

    map.addListener('zoom_changed', () => {
      const zoom = map?.getZoom()

      if (lastZoom && zoom && lastZoom > zoom) {
        hideMarkerPopup({ visiblePopup, selectedId })
      }

      drawMarkersAndClusters()

      lastZoom = zoom
    })

    map.addListener('click', () => {
      hideMarkerPopup({ visiblePopup, selectedId })
    })
  }

  watch(
    () => selectedId.value,
    () => {
      updateUrlQueryParameters()
    }
  )

  function updateUrlQueryParameters() {
    const zoom = map?.getZoom()
    const center = map?.getCenter()?.toJSON()
    const openPopupId = selectedId.value

    updateUrlQuery.setQueryByKey({
      key: 'map',
      value: {
        zoom,
        center,
        popupId: openPopupId,
      },
    })
  }

  let filterSubmitTimeout: ReturnType<typeof setTimeout> | null = null
  function updateFilterByMapBounds(timeout: number = 200) {
    const bounds = map?.getBounds()?.toJSON()

    if (!bounds) return

    filterState.actions.updateValue('latitude_from', bounds?.south)
    filterState.actions.updateValue('latitude_to', bounds?.north)
    filterState.actions.updateValue('longitude_from', bounds?.west)
    filterState.actions.updateValue('longitude_to', bounds?.east)

    if (filterSubmitTimeout) {
      clearTimeout(filterSubmitTimeout)
    }

    filterSubmitTimeout = setTimeout(() => {
      filterSubmitTimeout = null
      filterState.actions.submitFilter()

      updateUrlQueryParameters()
    }, timeout)
  }

  function initiallyCenterMapByMarkersBounds() {
    const filterValues = filterState.filterValues.value

    if (
      filterValues.latitude_from &&
      filterValues.latitude_to &&
      filterValues.longitude_from &&
      filterValues.longitude_to
    ) {
      return
    }

    if (!map || !mapCluster) {
      console.warn(
        'initiallyCenterMapByMarkersBounds: map or mapCluster is not set'
      )

      return
    }

    const zoom = map.getZoom()

    if (zoom === undefined) {
      removeAllMarkers()
      removeAllClusterMarkers()

      return
    }

    const dataFromCluster = mapCluster.getClusters([-180, -85, 180, 85], 20)

    if (dataFromCluster.length === 0) {
      return
    }

    const bounds = new google.maps.LatLngBounds()

    for (const item of dataFromCluster) {
      bounds.extend(
        new google.maps.LatLng(
          item.geometry.coordinates[1],
          item.geometry.coordinates[0]
        )
      )
    }

    map.fitBounds(bounds)
  }

  async function setMarkersData(data: EstateListingEssentialData[]) {
    mapCluster = getClusterFromItems(data)

    if (!map || !data) {
      return
    }

    await drawMarkersAndClusters()

    if (data.length === 0) {
      return
    }

    if (!isInitialized.value) {
      if (initialValues.popupId) {
        const marker = markers.find(
          (marker) =>
            marker.clusterData.properties.dataItem.id === initialValues.popupId
        )

        if (marker) {
          marker.openPopup()
        }
      }

      if (!updateUrlQuery.hasQueryByKey('map')) {
        initiallyCenterMapByMarkersBounds()
      }

      map.addListener('zoom_changed', () => {
        updateFilterByMapBounds(0)
      })

      map.addListener('bounds_changed', () => {
        updateFilterByMapBounds()
      })

      map.addListener('idle', () => {
        updateFilterByMapBounds()
      })

      map.addListener('dragend', () => {
        updateFilterByMapBounds(0)
      })

      isInitialized.value = true
    }
  }

  async function drawMarkersAndClusters() {
    if (!map || !mapCluster || !googleMaps.instance.value) {
      console.warn(
        'drawMarkersAndClusters: map, mapCluster or googleMaps is not set'
      )

      return
    }

    const zoom = map.getZoom()

    if (zoom === undefined) {
      removeAllMarkers()
      removeAllClusterMarkers()

      console.warn('drawMarkersAndClusters: zoom is not set')

      return
    }

    const dataFromCluster = mapCluster.getClusters([-180, -85, 180, 85], zoom)
    const markerPinIcons = {
      default: {
        url: '/icons/map/pin.png',
        scaledSize: new google.maps.Size(30, 42),
      },
      hover: {
        url: '/icons/map/pin-hover.png',
        scaledSize: new google.maps.Size(30, 42),
      },
    }

    const newMarkers = [] as MarkerType[]
    const newClusterMarkers = [] as ClusterMarkerType[]

    for (const clusterData of dataFromCluster) {
      if (clusterData.properties.cluster) {
        const marker = await getClusterMarker({
          map,
          mapCluster,
          clusterData,
        })

        newClusterMarkers.push({
          clusterData,
          marker,
        })
      } else {
        const { marker, openPopup } = getPinMarker({
          Marker: googleMaps.instance.value.marker.Marker,
          map,
          clusterData,
          markerPinIcons,
          visiblePopup,
          selectedId,
        })

        newMarkers.push({
          clusterData,
          marker,
          openPopup,
        })
      }
    }

    // remove current markers, that are not present in new markers
    for (let index = 0; index < markers.length; index++) {
      const marker = markers[index]

      if (
        !newMarkers.some(
          (newMarker) =>
            newMarker.clusterData.properties.dataItem.id ===
            marker.clusterData.properties.dataItem.id
        )
      ) {
        marker.marker.setMap(null)
        markers.splice(index, 1)
        index--
      }
    }

    // add new markers, that are not present in current markers
    for (const newMarker of newMarkers) {
      if (
        !markers.some(
          (marker) =>
            marker.clusterData.properties.dataItem.id ===
            newMarker.clusterData.properties.dataItem.id
        )
      ) {
        newMarker.marker.setMap(map)
        markers.push(newMarker)
      }
    }

    // remove current cluster markers, that are not present in new cluster markers
    for (let index = 0; index < clusterMarkers.length; index++) {
      const clusterMarker = clusterMarkers[index]

      if (
        !newClusterMarkers.some(
          (newClusterMarker) =>
            newClusterMarker.clusterData.id === clusterMarker.clusterData.id
        )
      ) {
        clusterMarker.marker.setMap(null)
        clusterMarkers.splice(index, 1)
        index--
      }
    }

    // add new cluster markers, that are not present in current cluster markers
    for (const newClusterMarker of newClusterMarkers) {
      if (
        !clusterMarkers.some(
          (clusterMarker) =>
            clusterMarker.clusterData.id === newClusterMarker.clusterData.id
        )
      ) {
        newClusterMarker.marker.setMap(map)
        clusterMarkers.push(newClusterMarker)
      }
    }
  }

  function removeAllMarkers() {
    for (const marker of markers) {
      marker.marker.setMap(null)
    }

    markers.length = 0
  }

  function removeAllClusterMarkers() {
    for (const clusterMarker of clusterMarkers) {
      clusterMarker.marker.setMap(null)
    }

    clusterMarkers.length = 0
  }

  function onUnmounted() {
    if (visiblePopup.value) {
      hideMarkerPopup({ visiblePopup, selectedId })
    }

    removeAllMarkers()
    removeAllClusterMarkers()

    if (map) {
      google.maps.event.clearInstanceListeners(map)
    }

    map = null
    mapCluster = null
  }

  return {
    loadLibs,
    initMap,
    initiallyCenterMapByMarkersBounds,
    setMarkersData,
    selectedId,
    onUnmounted,
  }
}
